// src/services/chat-api.ts
// API client for chat thread management (V2 endpoints)

import api from './api';
import type {
  ChatThread,
  ThreadListResponse,
  ThreadDetailResponse,
  ThreadQueryParams,
  NudgeCard,
} from '../types/chat';

/**
 * Chat API service — thread management, pending questions.
 * Streaming AI responses are handled by v2-chat-api.ts instead.
 */
export const chatApi = {
  /**
   * List chat threads for the current user
   */
  listThreads: async (params: ThreadQueryParams = {}): Promise<ThreadListResponse> => {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.data_source_account_id) queryParams.append('data_source_account_id', params.data_source_account_id);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    
    const queryString = queryParams.toString();
    const url = queryString ? `/api/v2/chat/threads?${queryString}` : '/api/v2/chat/threads';
    
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get a thread with its messages
   */
  getThread: async (threadId: string): Promise<ThreadDetailResponse> => {
    const response = await api.get(`/api/v2/chat/threads/${threadId}`);
    return response.data;
  },

  /**
   * Update a thread (title, status)
   */
  updateThread: async (
    threadId: string,
    updates: { title?: string; status?: 'active' | 'resolved' | 'archived' }
  ): Promise<{ success: boolean; data: ChatThread }> => {
    const response = await api.patch(`/api/v2/chat/threads/${threadId}`, updates);
    return response.data;
  },

  /**
   * Mark a thread as resolved
   */
  resolveThread: async (threadId: string): Promise<{ success: boolean; data: ChatThread }> => {
    const response = await api.post(`/api/v2/chat/threads/${threadId}/resolve`);
    return response.data;
  },

  /**
   * Archive a thread
   */
  archiveThread: async (threadId: string): Promise<{ success: boolean; data: ChatThread }> => {
    const response = await api.post(`/api/v2/chat/threads/${threadId}/archive`);
    return response.data;
  },

  /**
   * Delete a thread
   */
  deleteThread: async (threadId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/v2/chat/threads/${threadId}`);
    return response.data;
  },

  /**
   * Share a thread with the team (one-way, irreversible)
   */
  shareThread: async (threadId: string): Promise<{ success: boolean; data: ChatThread }> => {
    const response = await api.post(`/api/v2/chat/threads/${threadId}/share`);
    return response.data;
  },

  /**
   * List shared threads from other team members
   */
  listTeamThreads: async (params: ThreadQueryParams = {}): Promise<ThreadListResponse> => {
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const queryString = queryParams.toString();
    const url = queryString ? `/api/v2/chat/threads/team?${queryString}` : '/api/v2/chat/threads/team';

    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get threads with pending AI questions
   */
  getPendingQuestions: async (): Promise<{ success: boolean; data: ChatThread[]; count: number }> => {
    const response = await api.get('/api/v2/chat/pending-questions');
    return response.data;
  },

  /**
   * List AI-initiated threads in 'pending' state for the current user,
   * projected as UI-safe nudge cards. Drives the proactive nudge UI on
   * the chat home page.
   */
  listPendingThreads: async (
    limit = 10,
  ): Promise<{ success: boolean; data: NudgeCard[]; count: number }> => {
    const response = await api.get(`/api/v2/chat/threads/pending?limit=${limit}`);
    return response.data;
  },

  /**
   * Dismiss a pending AI question
   */
  dismissQuestion: async (threadId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/api/v2/chat/threads/${threadId}/dismiss-question`);
    return response.data;
  },
};

export default chatApi;
